/**
 * <Toolbar>: drawing tool selector.
 *
 * M2.T5 + M2.T6 + M2.T8 + M2.T9 + M2.T9.5 per §7.
 *
 * Shows: brush (3 sizes), eraser, fill (freeDraw only), undo, clear.
 * Active tool gets --smudge background + thicker SVG border (§13.15).
 * Hover inactive tools: scale(1.05).
 * Brush size changes rendered stroke width immediately via $brushSize store.
 */

import { useStore } from "@nanostores/preact";
import { $activeTool, $brushSize, $activeColor, BRUSH_SIZE_PX, PALETTE } from "./toolStore.js";
import type { DrawTool, BrushSizeIndex } from "./toolStore.js";
import type { BrandColor } from "@drawlol/shared";
import { colorName } from "../components/ColorReassignNotice.js";

type ToolbarProps = {
  /** Whether the fill bucket is available (Free Draw only). */
  fillEnabled?: boolean;
  onUndo?: () => void;
  /**
   * n7: Called on pointerdown, starts the 1.5s hold timer in useUndoClear.
   * Do NOT wire onClear (the fire callback) here; only onClearStart/End are
   * exposed so the hold logic can never be bypassed by a direct click.
   */
  onClearStart?: () => void;
  /**
   * Called on pointerup / pointerleave / pointercancel, cancels the hold
   * timer if the button is released before 1.5s.
   */
  onClearEnd?: () => void;
  /** True when the clear-hold is in progress (1.5s hold confirm). */
  clearHolding?: boolean;
};

/** Small SVG icons for each tool (hand-drawn style). */
/**
 * BrushIcon: chunky marker pen with a visible chiseled nib.
 *
 * Per user feedback (M2 critical fix): the prior icon was an abstract
 * squiggle that read like "fill" or "scribble," not like a drawing tool.
 * This version draws a marker barrel + cap + chiseled tip diagonally,
 * with the nib in the same `currentColor` so the brush color preview
 * stays implicit. Drawn at 24×24 logical, 20×20 displayed.
 */
function BrushIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    >
      {/* Marker barrel: diagonal from top-right (cap) to bottom-left (tip) */}
      <path
        d="M 17.5 3.5 L 21 7 L 9 19 L 4.5 19.5 L 5 15 L 17.5 3.5 Z"
        fill="currentColor"
        fillOpacity="0.12"
        strokeLinecap="round"
      />
      {/* Cap band (separates cap from barrel) */}
      <path d="M 15.5 5.5 L 19 9" strokeLinecap="round" />
      {/* Nib: the chiseled marker tip, filled solid so the drawing point is obvious */}
      <path d="M 4.5 19.5 L 9 19 L 7 21 L 4.5 19.5 Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * EraserIcon: Pink Pearl classroom-eraser silhouette.
 *
 * Per user feedback (reference image): a tilted rounded rectangle with
 * a light-gray top-right body and a pink bottom-left wedge, the iconic
 * "used end" of a school eraser. Thick outline. No crumbs / decorations.
 *
 * Two hard-coded colors override currentColor here: #e8e8e8 for the
 * neutral body and #e88d8d for the pink pad. Eraser identity > theme.
 */
function EraserIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      fill="none"
    >
      <g transform="rotate(-12 12 12)">
        {/* Body: rounded rectangle, light gray */}
        <rect x="3" y="9.5" width="18" height="8" rx="2" fill="#e8e8e8" />
        {/* Pink bottom-left wedge: apex at left-edge, base along bottom edge.
            The Q curve matches the rect's rx=2 corner so the wedge nestles
            into the rounded corner without poking out. */}
        <path
          d="M 3.5 12 L 3.5 15.5 Q 3.5 17.5 5.5 17.5 L 11.5 17.5 Z"
          fill="#e88d8d"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}

/**
 * FillIcon: tilted paint-bucket pouring out a single drop.
 *
 * Per user feedback (M4 batch 7 commit 4 reference image): the prior icon
 * was an angled paint roller, which read as "broom" or "lightning bolt"
 * once shrunk to 20×20. This version is a classic paint can: trapezoidal
 * body, oval rim and base, a wire handle arching over the top, and one
 * tear-shaped drop falling from the spout. Hand-drawn wobble, strokes
 * use currentColor with strokeLinecap=round / strokeLinejoin=round so
 * the silhouette matches the other tool icons (brush, eraser).
 */
function FillIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Bucket: tilted so the rim opens to the right (pouring) */}
      <g transform="rotate(20 12 12)">
        {/* Body: trapezoid, slightly wider at top (rim) */}
        <path d="M 5 7 L 19 7 L 17.5 19 L 6.5 19 Z" fill="currentColor" fillOpacity="0.12" />
        {/* Rim: oval band on top */}
        <ellipse cx="12" cy="7" rx="7" ry="1.5" />
        {/* Handle: wire arch over the rim */}
        <path d="M 6.5 6 Q 12 1 17.5 6" fill="none" />
      </g>
      {/* Falling drop: pear-shaped, single-color fill */}
      <path
        data-testid="fill-icon-drop"
        d="M 19 16 Q 17 18.5 19 20.5 Q 21 18.5 19 16 Z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}

function UndoIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M4 9 C4 6 7 4 11 4 C16 4 20 8 20 13 C20 18 16 21 11 21" strokeLinecap="round" />
      <polyline points="4,4 4,9 9,9" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <polyline points="3,6 5,6 21,6" />
      <path d="M19 6 L18 20 C18 21 17 22 16 22 L8 22 C7 22 6 21 6 20 L5 6" strokeLinecap="round" />
      <path d="M9 6 L9 4 L15 4 L15 6" />
    </svg>
  );
}

export function Toolbar({
  fillEnabled = false,
  onUndo,
  onClearStart,
  onClearEnd,
  clearHolding = false,
}: ToolbarProps) {
  const activeTool = useStore($activeTool);
  const brushSize = useStore($brushSize);
  const activeColor = useStore($activeColor);

  function toolClass(tool: DrawTool): string {
    const base = "toolbar-btn";
    return activeTool === tool ? `${base} toolbar-btn--active` : base;
  }

  function sizeClass(size: BrushSizeIndex): string {
    const base = "toolbar-size-btn";
    return activeTool === "brush" && brushSize === size ? `${base} toolbar-size-btn--active` : base;
  }

  return (
    <div class="toolbar" role="toolbar" aria-label="Drawing tools">
      {/* Brush tool */}
      <button
        class={toolClass("brush")}
        data-tool="brush"
        aria-label="Brush"
        aria-pressed={activeTool === "brush"}
        onClick={() => $activeTool.set("brush")}
      >
        <BrushIcon />
      </button>

      {/* Brush sizes: visible when brush is active */}
      {activeTool === "brush" && (
        <div class="toolbar-sizes" role="group" aria-label="Brush size">
          {([0, 1, 2] as BrushSizeIndex[]).map((sz) => (
            <button
              key={sz}
              class={sizeClass(sz)}
              data-size={sz}
              aria-label={["Thin", "Medium", "Thick"][sz]}
              aria-pressed={brushSize === sz}
              onClick={() => $brushSize.set(sz)}
            >
              <span
                class="brush-dot"
                style={{
                  // +4px legibility floor (audit 2026-06-11): the thin dot
                  // rendered 2px: invisible as a glyph. Display size only;
                  // the actual stroke width is BRUSH_SIZE_PX untouched.
                  width: `${BRUSH_SIZE_PX[sz] / 2 + 4}px`,
                  height: `${BRUSH_SIZE_PX[sz] / 2 + 4}px`,
                }}
              />
            </button>
          ))}
        </div>
      )}

      {/* Eraser tool */}
      <button
        class={toolClass("eraser")}
        data-tool="eraser"
        aria-label="Eraser"
        aria-pressed={activeTool === "eraser"}
        onClick={() => $activeTool.set("eraser")}
      >
        <EraserIcon />
      </button>

      {/* Fill bucket: Free Draw only */}
      {fillEnabled && (
        <button
          class={toolClass("fill")}
          data-tool="fill"
          aria-label="Fill bucket"
          aria-pressed={activeTool === "fill"}
          onClick={() => $activeTool.set("fill")}
        >
          <FillIcon />
        </button>
      )}

      <div class="toolbar-divider" />

      {/* Undo */}
      <button class="toolbar-btn" data-tool="undo" aria-label="Undo last stroke" onClick={onUndo}>
        <UndoIcon />
      </button>

      {/* Clear all: 1.5s hold confirm.
          n7: Use pointer events for hold detection; onClick would bypass the
          hold timer. onClearStart fires on pointerdown (starts timer) and
          onClearEnd fires on pointerup/leave/cancel (cancels timer if <1.5s).
          Keyboard (audit 2026-06-11): holding Enter/Space mirrors the pointer
          hold: keydown arms the timer (key-repeat ignored), keyup/blur
          cancels it, so the same 1.5s confirm applies without a mouse. */}
      <button
        class={clearHolding ? "toolbar-btn toolbar-btn--danger" : "toolbar-btn"}
        data-tool="clear"
        aria-label={clearHolding ? "Release to clear all" : "Hold to clear all"}
        onPointerDown={onClearStart}
        onPointerUp={onClearEnd}
        onPointerLeave={onClearEnd}
        onPointerCancel={onClearEnd}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !e.repeat) {
            e.preventDefault();
            onClearStart?.();
          }
        }}
        onKeyUp={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClearEnd?.();
          }
        }}
        onBlur={onClearEnd}
      >
        <ClearIcon />
      </button>

      <div class="toolbar-divider" />

      {/* 8-color palette: BRAND_COLORS is the one place the colors are defined */}
      <div class="toolbar-palette" role="radiogroup" aria-label="Color palette">
        {PALETTE.map((color: BrandColor) => (
          <button
            key={color}
            class={activeColor === color ? "color-swatch color-swatch--active" : "color-swatch"}
            role="radio"
            aria-checked={activeColor === color}
            tabIndex={activeColor === color ? 0 : -1}
            onKeyDown={(event) => {
              const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1
                : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 0;
              if (!step) return;
              event.preventDefault();
              const index = (PALETTE.indexOf(color) + step + PALETTE.length) % PALETTE.length;
              $activeColor.set(PALETTE[index]!);
              const buttons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
              buttons?.[index]?.focus();
            }}
            aria-label={colorName(color)}
            style={{ background: color }}
            onClick={() => $activeColor.set(color)}
          />
        ))}
      </div>
    </div>
  );
}
